import React, { useContext, useMemo } from 'react';
import { isEmpty } from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Collapse, TextTitle, useCollapseState, ValidatedPanel, getPanelErrorProps } from '@bingads-webui-campaign/wizard-jumbo-card';
import { SearchThemesImg } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { AudienceSignalPanel } from '@bingads-webui-campaign-react/step-wizard-controls';
import { AudienceSignalSection, getSearchThemeDetails, PANEL_KEYS } from '@bingads-webui-campaign-react/wizard-shared-components';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { ValidationSearchThemeSelector } from '@bingads-webui-campaign-react/search-theme-selector';
import { SingleLineShimmer } from '../../../shared/components/section-shimmer';

export const SignalSection = ({
  assetGroup,
  permissions,
}) => {
  const i18n = useContext(I18nContext);
  const isSearchThemeEnabled = permissions && permissions.dynamic && permissions.dynamic.SearchThemes;
  const isLoading = useMemo(() => isEmpty(assetGroup), [assetGroup]);
  const perfMarker = useContext(PerformanceMarkerContext);

  const [
    audienceSignalSections,
    audienceSignalExpandedSections,
    setaudienceSignalSettingsExpandedSections,
  ] = useCollapseState(
    isSearchThemeEnabled ? ['audience-signal', 'search-themes'] : ['audience-signal'] /* all sections */,
    isSearchThemeEnabled ? ['audience-signal', 'search-themes'] : ['audience-signal'] /* initially expanded sections */
  );

  const searchThemeSection = useMemo(() => (
    <div className={classNames('asset-group-search-theme-section')}>
      {isLoading ? <SingleLineShimmer /> :
      (<ValidationSearchThemeSelector
        i18n={i18n}
        wizardPerfMarker={perfMarker}
        searchThemes={assetGroup && assetGroup.data && assetGroup.data.AssetGroupSearchThemes}
      />)
      }
    </div>
  ), [assetGroup, i18n, isLoading, perfMarker]);

  const signalSection = useMemo(() => (
    <div className={classNames('more-settings-section')}>
      <AudienceSignalSection>
        <Collapse
          allPanelItems={audienceSignalSections}
          checkItems
          skipItemsTypeCheck
          activeKeys={audienceSignalExpandedSections}
          setActiveKeys={setaudienceSignalSettingsExpandedSections}
        >
          <AudienceSignalPanel
            key={PANEL_KEYS.AUDIENCE_SIGNAL}
            isLoading={isLoading}
            options={{ wizardPerfMarker: perfMarker }}
            data={assetGroup.data}
          />
        </Collapse>
      </AudienceSignalSection>
      {isSearchThemeEnabled &&
      <Collapse
        allPanelItems={audienceSignalSections}
        checkItems
        skipItemsTypeCheck
        activeKeys={audienceSignalExpandedSections}
        setActiveKeys={setaudienceSignalSettingsExpandedSections}
      >
        <ValidatedPanel
          key="search-themes"
          title={<TextTitle
            titleKey={_TL_('Search Themes')}
            topicKey="pop_BA_PMAX_SearchThemes"
            details={getSearchThemeDetails(i18n, assetGroup && assetGroup.data && assetGroup.data.AssetGroupSearchThemes)}
            icon={SearchThemesImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{ expandKey: 'search-themes' }}
        >
          {searchThemeSection}
        </ValidatedPanel>
      </Collapse>}
    </div>
  ), [audienceSignalSections, audienceSignalExpandedSections, setaudienceSignalSettingsExpandedSections, isLoading, perfMarker, assetGroup, isSearchThemeEnabled, i18n, searchThemeSection]);

  return signalSection;
};

SignalSection.propTypes = {
  assetGroup: PropTypes.shape({}).isRequired,
  campaignTimeZone: PropTypes.string.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      SearchThemes: PropTypes.bool,
    }).isRequired,
  }).isRequired,
};
